import { injectable } from "tsyringe";
import { ProductCategories } from "src/constants/product-categories";
import { autoBind } from "src/shared/decorators/autobind";
import {
    AutosProducts,
    EPSProducts,
    HealthProducts,
} from "src/constants/product-codes";
import {
    EpsPlan,
    HealthPolicy,
    HealthPolicyDetail,
    PgaPolicy,
    PgaPolicyDetail,
    PolicyProduct,
    VehiclePolicy,
    VehiclePolicyDetail,
} from "src/shared/models";
import { ProductFeatures } from "src/shared/models/policy-features";

@injectable()
export class PolicyBridgeService {
    @autoBind
    standarizePolicy(rawPolicy: Record<string, any>): PgaPolicy {
        if (Object.values(AutosProducts).includes(rawPolicy.productCode)) {
            const product = PolicyProduct.fromJson<PolicyProduct>({
                category: ProductCategories.AUTOS,
                code: rawPolicy.productCode,
                description: rawPolicy.offer.description,
                systemCode: rawPolicy.systemCode,
                features: {
                    ...rawPolicy.productFeatures,
                    displayPayments: rawPolicy.displayPayments,
                    displayInfo: rawPolicy.displayInfo,
                },
            });
            return VehiclePolicy.fromJson<VehiclePolicy>({
                id: rawPolicy.id,
                number: rawPolicy.number,
                currency: rawPolicy.currency,
                insuredRole: rawPolicy.insuredRole,
                validityStart: rawPolicy.validityStart,
                validityEnd: rawPolicy.validityEnd,
                status: rawPolicy.status,
                offer: rawPolicy.offer,
                product,
            });
        }
        if (Object.values(EPSProducts).includes(rawPolicy.product.code)) {
            const features = new ProductFeatures().toJson();
            rawPolicy.product.features.forEach((feature: string) => {
                features[feature] = true;
            });
            return EpsPlan.fromJson<EpsPlan>({
                ...rawPolicy,
                product: {
                    ...rawPolicy.product,
                    features,
                },
            });
        }
        if (Object.values(HealthProducts).includes(rawPolicy.product.code)) {
            const features = new ProductFeatures().toJson();
            rawPolicy.product.features.forEach((feature: string) => {
                features[feature] = true;
            });
            return HealthPolicy.fromJson<HealthPolicy>({
                ...rawPolicy,
                product: {
                    ...rawPolicy.product,
                    features,
                },
            });
        }
        const product = PolicyProduct.fromJson<PolicyProduct>({
            ...rawPolicy.product,
            features: {
                ...rawPolicy.productFeatures,
                displayPayments: rawPolicy.displayPayments,
                displayInfo: rawPolicy.displayInfo,
            },
        });
        return PgaPolicy.fromJson<PgaPolicy>({
            ...rawPolicy,
            product: product.toJson(),
        });
    }

    @autoBind
    standarizePolicyDetail<T extends PgaPolicy>(
        rawPolicy: Record<string, any>
    ): T {
        if (Object.values(HealthProducts).includes(rawPolicy.productCode)) {
            const product: Record<string, any> = {
                category: ProductCategories.SALUD,
                code: rawPolicy.productCode,
                description: rawPolicy.product,
                systemCode: rawPolicy.systemCode,
                features: {
                    ...rawPolicy.productFeatures,
                    displayPayments: rawPolicy.displayPayments,
                    displayInfo: true,
                },
            };
            delete rawPolicy.displayPayments;
            delete rawPolicy.productFeatures;
            const paymentPlan: Record<string, any> = {
                hasDebitMembership: rawPolicy.hasDebitMembership,
                paymentPlan: rawPolicy.paymentPlan,
                totalPremium: rawPolicy.premium,
                paymentWay: rawPolicy.paymentWay,
                description: rawPolicy.plan,
            };
            const policy: Record<string, any> = {
                id: rawPolicy.policyId,
                number: rawPolicy.number,
                currency: rawPolicy.currency,
                insuredRole: rawPolicy.insuredRole,
                status: rawPolicy.status,
                codPlan: rawPolicy.codPlan,
                revPlan: rawPolicy.revPlan,
                holderId: rawPolicy.holderId,
                contractorId: rawPolicy.contractorId,
                validityStart: rawPolicy.validityStart,
                insuredAmount: rawPolicy.insuredAmount,
                insuredCode: rawPolicy.insuredCode,
                validityEnd: rawPolicy.validityEnd,
                product,
                payment: paymentPlan,
                paymentPlan, // TODO - Revisar si se debe usar "paymentPlan" en lugar de "payment"
            };
            return HealthPolicyDetail.fromJson<HealthPolicyDetail>(
                policy
            ) as unknown as T;
        }
        if (Object.values(AutosProducts).includes(rawPolicy.productCode)) {
            const product: Record<string, any> = {
                category: ProductCategories.AUTOS,
                code: rawPolicy.productCode,
                description: rawPolicy.offer.description,
                systemCode: rawPolicy.systemCode,
                features: {
                    ...rawPolicy.productFeatures,
                    displayPayments: rawPolicy.displayPayments,
                    displayInfo: true,
                },
            };
            delete rawPolicy.displayPayments;
            delete rawPolicy.productFeatures;
            const paymentPlan: Record<string, any> = {
                hasDebitMembership: rawPolicy.hasDebitMembership,
                paymentPlan: rawPolicy.paymentPlan,
                totalPremium: rawPolicy.premium,
                paymentWay: rawPolicy.paymentWay,
                description: rawPolicy.plan,
            };
            const policy: Record<string, any> = {
                category: rawPolicy.category,
                broker: rawPolicy.broker,
                drivenCity: rawPolicy.drivenCity,
                id: rawPolicy.id,
                number: rawPolicy.number,
                currency: rawPolicy.currency,
                insuredRole: rawPolicy.insuredRole,
                contractorId: rawPolicy.contractorId,
                insuredAmount: rawPolicy.insuredAmount,
                insuredCode: rawPolicy.insuredCode,
                validityStart: rawPolicy.validityStart,
                validityEnd: rawPolicy.validityEnd,
                status: rawPolicy.status,
                offer: rawPolicy.offer,
                product,
                paymentPlan,
            };
            return VehiclePolicyDetail.fromJson<VehiclePolicyDetail>(
                policy
            ) as unknown as T;
        }
        const product = {
            ...rawPolicy.product,
            features: {
                ...rawPolicy.productFeatures,
                displayPayments: rawPolicy.displayPayments,
                displayInfo: true,
            },
        };
        delete rawPolicy.displayPayments;
        delete rawPolicy.productFeatures;
        return PgaPolicyDetail.fromJson<PgaPolicyDetail>({
            ...rawPolicy,
            product,
        }) as unknown as T;
    }

    @autoBind
    getPoliciesByCategory<T extends PgaPolicy>(
        policies: Array<PgaPolicy>,
        category: string
    ): T[] {
        return policies.filter(
            (policy) => policy.product.category === category
        ) as T[];
    }
}
